import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, ShoppingCart, Menu, X, ChevronDown, Star, Heart, ArrowRight, Package,
  Truck, Globe, Headphones, Facebook, Instagram, Youtube, Linkedin, MessageCircle,
  MapPin, Phone, Mail, BadgeCheck, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, imgUrl } from "@/lib/api";

export const DP = {
  navy: "#0B192C", royal: "#1D4ED8", bright: "#2563EB", green: "#10B981", greenD: "#059669",
  ice: "#F0F7FF", surface: "#F8FAFC", footer: "#0A121E", text: "#0F172A", muted: "#64748B", border: "#E2E8F0",
};

export const IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/7973de61-e809-472b-a7ce-3b1d3e08de6a/images/3d94f8bc2ef1c15cfbedac6f78fcbadef572822c07ec6e79432f2abee5fb90fc.jpeg",
  store: "https://static.prod-images.emergentagent.com/jobs/7973de61-e809-472b-a7ce-3b1d3e08de6a/images/3f5e135461efce058a1d9e229f113ff9e3f5f31ff4aa3099ccbbcaf413491f79.jpeg",
  marketplaces: "https://static.prod-images.emergentagent.com/jobs/7973de61-e809-472b-a7ce-3b1d3e08de6a/images/fd5ba22eab4af6cc3571de76b3d19b0d88559e229b8805a201ccd374960fcb8a.jpeg",
  trending: "https://static.prod-images.emergentagent.com/jobs/7973de61-e809-472b-a7ce-3b1d3e08de6a/images/fe36aa4982029cf9672bd43fb9556ba7d4a47bef6da54a39f363afc75f88a6a6.jpeg",
  mega: "https://static.prod-images.emergentagent.com/jobs/7973de61-e809-472b-a7ce-3b1d3e08de6a/images/fa89169989c3be5cd6c29782bfdbf96048d0a4a86f1ac6cb79430c3507b3e9f2.jpeg",
  rocket: "https://static.prod-images.emergentagent.com/jobs/7973de61-e809-472b-a7ce-3b1d3e08de6a/images/9b3338d671cffaa7b1f5e5f551eec718df609b4aeb897dfbe874ec58ece7a4c4.jpeg",
  journey: "https://static.prod-images.emergentagent.com/jobs/7973de61-e809-472b-a7ce-3b1d3e08de6a/images/477d1f81e5538615659772e527d61917e345e206a98f6371cf1a85af6e1b68d4.jpeg",
  office: "https://static.prod-images.emergentagent.com/jobs/7973de61-e809-472b-a7ce-3b1d3e08de6a/images/fc2915dda621eeb309a7f8514bae5cf517d85fd729b9236fa89fc66f18796554.jpeg",
};

export const LOGO = "/droppartner-logo.png";

// ---------- cart (localStorage, public browsing cart) ----------
const CART_KEY = "dp_cart";
export const getCart = () => JSON.parse(localStorage.getItem(CART_KEY) || "[]");
export const cartCount = () => getCart().reduce((s, i) => s + i.qty, 0);
export function addToCart(product, qty = 1) {
  const cart = getCart();
  const ex = cart.find((i) => i.id === product.id);
  if (ex) ex.qty += qty;
  else cart.push({ id: product.id, name: product.name, price: product.price, mrp: product.mrp || 0, image: (product.images || [])[0] || "", qty });
  localStorage.setItem(CART_KEY, JSON.stringify(cart));
  window.dispatchEvent(new Event("dp-cart"));
}

// ---------- primitives ----------
export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, delay }} className={className}>
    {children}
  </motion.div>
);

export function SectionHead({ eyebrow, title, sub, center = true, light = false }) {
  return (
    <Reveal className={center ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}>
      {eyebrow && <p className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: light ? "#6EE7B7" : DP.green }}>{eyebrow}</p>}
      <h2 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl" style={{ fontFamily: "Outfit, sans-serif", color: light ? "#fff" : DP.text }}>{title}</h2>
      {sub && <p className="mt-3 text-sm leading-relaxed sm:text-base" style={{ color: light ? "#C7D6EA" : DP.muted }}>{sub}</p>}
    </Reveal>
  );
}

export function Rating({ value = 4.5, count }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="flex">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star key={i} size={12} className={i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
        ))}
      </span>
      <span className="text-[11px] font-semibold text-slate-500">{value}{count != null && ` (${count})`}</span>
    </span>
  );
}

export function Counter({ to, suffix = "", duration = 1400 }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    let started = false;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started) {
        started = true;
        const t0 = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - t0) / duration);
          setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to, duration]);
  return <span ref={ref}>{val.toLocaleString("en-IN")}{suffix}</span>;
}

export function GreenBtn({ to, children, testid, className = "", onClick }) {
  const cls = `group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white shadow-[0_10px_26px_rgba(16,185,129,0.35)] transition-transform hover:scale-[1.04] ${className}`;
  const inner = <>{children} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></>;
  return to
    ? <Link to={to} data-testid={testid} className={cls} style={{ background: DP.green }} onClick={onClick}>{inner}</Link>
    : <button data-testid={testid} className={cls} style={{ background: DP.green }} onClick={onClick}>{inner}</button>;
}

export function GhostBtn({ to, children, testid, className = "" }) {
  return (
    <Link to={to} data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 rounded-full border-2 px-7 py-3.5 text-sm font-bold transition-colors hover:bg-blue-50 ${className}`}
      style={{ borderColor: DP.royal, color: DP.royal }}>
      {children}
    </Link>
  );
}

// ---------- product card ----------
export function ProductCard({ p, onWishlist }) {
  const [wished, setWished] = useState(false);
  return (
    <Reveal className="h-full">
      <div data-testid={`product-card-${p.id}`}
        className="group flex h-full flex-col overflow-hidden rounded-2xl border bg-white shadow-[0_4px_18px_rgba(11,25,44,0.05)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_44px_rgba(11,25,44,0.13)]"
        style={{ borderColor: DP.border }}>
        <div className="relative">
          <Link to={`/product/${p.id}`} data-testid={`product-link-${p.id}`}>
            {p.images?.[0]
              ? <img src={imgUrl(p.images[0])} alt={p.name} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              : <div className="flex aspect-square w-full items-center justify-center" style={{ background: DP.ice }}><Package size={34} className="text-blue-200" /></div>}
          </Link>
          {p.discount_pct > 0 && (
            <span data-testid={`discount-badge-${p.id}`} className="absolute left-2.5 top-2.5 rounded-full px-2.5 py-1 text-[10px] font-extrabold text-white shadow" style={{ background: DP.green }}>
              {p.discount_pct}% OFF
            </span>
          )}
          <button data-testid={`wishlist-${p.id}`} aria-label="wishlist"
            onClick={() => { setWished(!wished); toast.success(wished ? "Removed from wishlist" : "Added to wishlist"); onWishlist?.(p); }}
            className="absolute right-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-full bg-white/95 shadow transition-transform hover:scale-110">
            <Heart size={15} className={wished ? "fill-rose-500 text-rose-500" : "text-slate-400"} />
          </button>
        </div>
        <div className="flex flex-1 flex-col p-3.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: DP.bright }}>{p.category}</p>
          <Link to={`/product/${p.id}`} className="mt-0.5 line-clamp-2 text-[13px] font-semibold leading-snug transition-colors hover:text-[#1D4ED8]" style={{ color: DP.text }}>
            {p.name}
          </Link>
          <div className="mt-1.5"><Rating value={p.rating || 4.3} count={p.reviews_count ?? 128} /></div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-base font-extrabold" style={{ color: DP.text }}>{fmtINR(p.price)}</span>
            {p.mrp > p.price && <span className="text-xs text-slate-400 line-through">{fmtINR(p.mrp)}</span>}
          </div>
          <button data-testid={`add-cart-${p.id}`} onClick={() => { addToCart(p); toast.success("Added to cart"); }}
            className="mt-3 rounded-lg py-2 text-xs font-bold text-white transition-all hover:brightness-110 active:scale-95"
            style={{ background: DP.royal }}>
            Add to Cart
          </button>
        </div>
      </div>
    </Reveal>
  );
}

// ---------- chrome: topbar / header / footer ----------
const NAV = [
  ["Home", "/"], ["Shop", "/shop"], ["Categories", "/categories"], ["Deals", "/deals"],
  ["How It Works", "/how-it-works"], ["For Partners", "/partner-plans"], ["Blog", "/blog"],
  ["About Us", "/about-us"], ["Contact Us", "/contact-us"],
];

export function TopBar() {
  return (
    <div className="hidden text-[11px] text-blue-100 md:block" style={{ background: DP.navy }} data-testid="topbar">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5">
        <div className="flex items-center gap-4">
          <span className="font-semibold text-white">🇮🇳 India's Trusted Dropshipping Partner</span>
          <span className="hidden items-center gap-3 lg:flex">
            <span className="flex items-center gap-1"><BadgeCheck size={11} style={{ color: DP.green }} /> Zero Inventory</span>
            <span className="flex items-center gap-1"><Package size={11} className="text-amber-400" /> Premium Products</span>
            <span className="flex items-center gap-1"><Globe size={11} className="text-sky-300" /> Multi-Marketplace</span>
            <span className="flex items-center gap-1"><Headphones size={11} className="text-emerald-300" /> Dedicated Support</span>
          </span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/help-center" className="transition-colors hover:text-white" data-testid="topbar-help">Help Center</Link>
          <span className="flex cursor-pointer items-center gap-0.5">English <ChevronDown size={10} /></span>
        </div>
      </div>
    </div>
  );
}

export function Header() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(cartCount());
  useEffect(() => {
    const fn = () => setCount(cartCount());
    window.addEventListener("dp-cart", fn);
    return () => window.removeEventListener("dp-cart", fn);
  }, []);
  const doSearch = (e) => {
    e.preventDefault();
    nav(q.trim() ? `/shop?search=${encodeURIComponent(q.trim())}` : "/shop");
    setOpen(false);
  };
  return (
    <header className="sticky top-0 z-50 border-b bg-white/95 shadow-[0_2px_16px_rgba(11,25,44,0.05)] backdrop-blur-md" style={{ borderColor: DP.border }} data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5">
        <Link to="/" data-testid="header-logo" className="flex shrink-0 items-center gap-2">
          <img src={LOGO} alt="DropPartner" className="h-11 w-auto rounded-lg object-contain" />
          <span className="hidden sm:block">
            <span className="block text-lg font-extrabold leading-none" style={{ fontFamily: "Outfit, sans-serif" }}>
              <span style={{ color: DP.royal }}>Drop</span><span style={{ color: DP.green }}>Partner</span>
            </span>
            <span className="mt-0.5 block text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: DP.muted }}>Choose. Sell. Grow.</span>
          </span>
        </Link>
        <form onSubmit={doSearch} className="hidden flex-1 md:block" data-testid="header-search-form">
          <div className="mx-auto flex max-w-xl overflow-hidden rounded-full border-2 bg-white" style={{ borderColor: DP.border }}>
            <input data-testid="header-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search products, categories, brands…" className="w-full px-5 py-2 text-sm outline-none" style={{ color: DP.text }} />
            <button data-testid="header-search-btn" className="px-5 text-white" style={{ background: DP.green }}><Search size={16} /></button>
          </div>
        </form>
        <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-3">
          <Link to="/become-a-partner" data-testid="header-become-partner" className="hidden text-xs font-bold transition-colors lg:block" style={{ color: DP.royal }}>Become a Partner</Link>
          <Link to="/login" data-testid="header-login" className="hidden rounded-full px-4 py-2 text-xs font-bold transition-colors sm:block" style={{ color: DP.text }}>Login</Link>
          <Link to="/register" data-testid="header-join" className="hidden rounded-full px-5 py-2.5 text-xs font-extrabold text-white shadow-[0_6px_18px_rgba(16,185,129,0.4)] transition-transform hover:scale-105 sm:block" style={{ background: DP.green }}>Join Now</Link>
          <Link to="/cart" data-testid="header-cart" className="relative p-2" style={{ color: DP.text }}>
            <ShoppingCart size={20} />
            {count > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-extrabold text-white" style={{ background: DP.green }}>{count}</span>}
          </Link>
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="p-2 md:hidden" style={{ color: DP.text }} aria-label="menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <nav className="hidden border-t md:block" style={{ borderColor: DP.border }} data-testid="main-nav">
        <div className="mx-auto flex max-w-7xl items-center gap-1 px-4">
          {NAV.map(([l, p], i) => (
            <NavLink key={p} to={p} end={p === "/"} data-testid={`nav-${l.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => `rounded-full px-3.5 py-2 text-[13px] font-bold transition-colors ${isActive ? "text-white" : "hover:bg-blue-50"}`}
              style={({ isActive }) => ({ background: isActive ? DP.royal : "transparent", color: isActive ? "#fff" : i === 0 ? DP.royal : DP.text })}>
              {l}
            </NavLink>
          ))}
          <span className="ml-auto flex items-center gap-4 py-1 text-[12px] font-semibold" style={{ color: DP.muted }}>
            <Link to="/track-order" data-testid="nav-track" className="flex items-center gap-1 transition-colors hover:text-[#1D4ED8]"><Truck size={13} /> Track Order</Link>
            <Link to="/help-center" data-testid="nav-help" className="flex items-center gap-1 transition-colors hover:text-[#1D4ED8]"><Headphones size={13} /> Help Center</Link>
          </span>
        </div>
      </nav>
      {open && (
        <div className="border-t bg-white px-4 pb-5 pt-3 md:hidden" style={{ borderColor: DP.border }} data-testid="mobile-menu">
          <form onSubmit={doSearch} className="mb-3 flex overflow-hidden rounded-full border-2" style={{ borderColor: DP.border }}>
            <input data-testid="mobile-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="w-full px-4 py-2 text-sm outline-none" />
            <button className="px-4 text-white" style={{ background: DP.green }}><Search size={15} /></button>
          </form>
          <div className="grid grid-cols-2 gap-1">
            {NAV.map(([l, p]) => (
              <Link key={p} to={p} onClick={() => setOpen(false)} data-testid={`mnav-${l.toLowerCase().replace(/ /g, "-")}`}
                className="rounded-lg px-3 py-2.5 text-sm font-bold transition-colors hover:bg-blue-50" style={{ color: DP.text }}>{l}</Link>
            ))}
            <Link to="/track-order" onClick={() => setOpen(false)} data-testid="mnav-track" className="rounded-lg px-3 py-2.5 text-sm font-bold" style={{ color: DP.text }}>Track Order</Link>
            <Link to="/help-center" onClick={() => setOpen(false)} data-testid="mnav-help" className="rounded-lg px-3 py-2.5 text-sm font-bold" style={{ color: DP.text }}>Help Center</Link>
          </div>
          <div className="mt-3 flex gap-2">
            <Link to="/login" onClick={() => setOpen(false)} data-testid="mnav-login" className="flex-1 rounded-full border-2 py-2.5 text-center text-sm font-bold" style={{ borderColor: DP.royal, color: DP.royal }}>Login</Link>
            <Link to="/register" onClick={() => setOpen(false)} data-testid="mnav-join" className="flex-1 rounded-full py-2.5 text-center text-sm font-extrabold text-white" style={{ background: DP.green }}>Join Now</Link>
          </div>
        </div>
      )}
    </header>
  );
}

export function Newsletter() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/newsletter", { email });
      toast.success("Subscribed — welcome aboard!");
      setEmail("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not subscribe"); } finally { setBusy(false); }
  };
  return (
    <section className="py-14" style={{ background: DP.navy }} data-testid="newsletter-section">
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 px-4 lg:flex-row lg:justify-between">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: DP.royal }}><Mail size={22} className="text-white" /></span>
          <div>
            <h3 className="text-lg font-extrabold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Stay Ahead. Sell Smarter.</h3>
            <p className="text-xs text-blue-200">Product updates, ecommerce tips, offers and business insights.</p>
          </div>
        </div>
        <form onSubmit={subscribe} className="flex w-full max-w-md overflow-hidden rounded-full bg-white p-1 shadow-xl">
          <input data-testid="newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address" className="w-full px-4 py-2.5 text-sm outline-none" />
          <button data-testid="newsletter-subscribe-btn" disabled={busy}
            className="shrink-0 rounded-full px-6 py-2.5 text-sm font-extrabold text-white transition-transform hover:scale-105" style={{ background: DP.green }}>
            {busy ? "…" : "Subscribe"}
          </button>
        </form>
        <div className="flex items-center gap-2" data-testid="social-links">
          <span className="mr-1 text-xs font-semibold text-blue-200">Follow Us</span>
          {[[Facebook, "facebook"], [Instagram, "instagram"], [Youtube, "youtube"], [Linkedin, "linkedin"], [MessageCircle, "whatsapp"]].map(([Icon, n]) => (
            <a key={n} href="#" aria-label={n} data-testid={`social-${n}`}
              className="flex h-9 w-9 items-center justify-center rounded-full text-white transition-transform hover:scale-110" style={{ background: "#16283F" }}>
              <Icon size={15} />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

const FOOTER_COLS = [
  ["Shop", [["All Products", "/shop"], ["Categories", "/categories"], ["Deals & Offers", "/deals"], ["New Arrivals", "/shop?sort=newest"], ["Best Sellers", "/shop?sort=rating"]]],
  ["For Partners", [["How It Works", "/how-it-works"], ["Partner Plans", "/partner-plans"], ["Become a Partner", "/become-a-partner"], ["Partner Login", "/login"], ["Success Stories", "/about-us"]]],
  ["Support", [["Help Center", "/help-center"], ["Track Order", "/track-order"], ["FAQ", "/faq"], ["Contact Us", "/contact-us"], ["Return Policy", "/return-policy"], ["Shipping Information", "/shipping-policy"]]],
  ["Company", [["About Us", "/about-us"], ["Blog", "/blog"], ["Terms & Conditions", "/terms-and-conditions"], ["Privacy Policy", "/privacy-policy"], ["Cancellation Policy", "/cancellation-policy"]]],
];

export function Footer() {
  return (
    <footer className="text-blue-200" style={{ background: DP.footer }} data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:grid-cols-2 lg:grid-cols-6">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="DropPartner" className="h-11 w-auto rounded-lg bg-white object-contain p-0.5" />
            <span>
              <span className="block text-lg font-extrabold leading-none text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                <span className="text-sky-400">Drop</span><span style={{ color: DP.green }}>Partner</span>
              </span>
              <span className="mt-0.5 block text-[9px] font-bold uppercase tracking-[0.2em] text-blue-300">Choose. Sell. Grow.</span>
            </span>
          </div>
          <p className="mt-4 max-w-xs text-xs leading-relaxed text-blue-300/80">Your trusted partner for online selling — zero inventory, premium products, marketplace reach and your own store.</p>
          <div className="mt-5 space-y-2 text-xs">
            <p className="flex items-center gap-2"><Phone size={13} style={{ color: DP.green }} /> +91 90000 00000</p>
            <p className="flex items-center gap-2"><Mail size={13} style={{ color: DP.green }} /> support@droppartner.in</p>
            <p className="flex items-center gap-2"><MapPin size={13} style={{ color: DP.green }} /> Hyderabad, Telangana, India</p>
          </div>
          <div className="mt-5 flex items-center gap-2 rounded-xl border border-[#1B2C44] p-3">
            <ShieldCheck size={18} style={{ color: DP.green }} />
            <span className="text-[11px] font-semibold text-blue-100">100% Secure Payments · Trusted by Indian entrepreneurs</span>
          </div>
        </div>
        {FOOTER_COLS.map(([title, links]) => (
          <div key={title}>
            <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.18em] text-white">{title}</p>
            <ul className="space-y-2">
              {links.map(([l, p]) => (
                <li key={l}><Link to={p} data-testid={`footer-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-xs text-blue-300/80 transition-colors hover:text-white">{l}</Link></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-[#16283F]">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-5 text-[11px] text-blue-300/60 sm:flex-row">
          <p>© 2026 DropPartner. All Rights Reserved.</p>
          <p className="flex flex-wrap justify-center gap-x-3">
            {[["Terms & Conditions", "/terms-and-conditions"], ["Privacy Policy", "/privacy-policy"], ["Return Policy", "/return-policy"], ["Shipping Policy", "/shipping-policy"], ["Cancellation Policy", "/cancellation-policy"]].map(([l, p]) => (
              <Link key={p} to={p} className="transition-colors hover:text-white">{l}</Link>
            ))}
          </p>
          <p>Made with ❤️ for Indian Entrepreneurs</p>
        </div>
      </div>
    </footer>
  );
}

export function PageHero({ title, sub, crumbs = [] }) {
  return (
    <section className="relative overflow-hidden py-14 sm:py-20" style={{ background: `linear-gradient(135deg, ${DP.navy} 0%, ${DP.royal} 70%, ${DP.bright} 100%)` }} data-testid="page-hero">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-20 blur-3xl" style={{ background: DP.green }} />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full opacity-20 blur-3xl" style={{ background: DP.bright }} />
      <div className="relative mx-auto max-w-7xl px-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-emerald-300" data-testid="breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link>
          {crumbs.map((c) => <span key={c}> <span className="text-blue-300">/</span> {c}</span>)}
        </p>
        <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h1>
        {sub && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-blue-100 sm:text-base">{sub}</p>}
      </div>
    </section>
  );
}

export function SiteShell({ children }) {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif", color: DP.text }} data-testid="dp-site">
      <TopBar />
      <Header />
      <main>{children}</main>
      <Newsletter />
      <Footer />
      <style>{`@keyframes dpfloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }`}</style>
    </div>
  );
}
